import React, { useState, Fragment } from 'react';
import { Store, Product, Gift } from '../../types';
import { STORES, PRODUCTS, MOCK_GIFTS } from '../../data/mockData';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useToast } from '../ToastContext';
import { Gift as GiftIcon, ChevronRight, Check, ArrowLeft, ChevronDown, Search, User, PartyPopper, Link as LinkIcon } from 'lucide-react';
import { ProductCard } from '../ProductCard';
interface GiftingScreenProps {
  onNavigate: (screen: string, data?: any) => void;
}
type GiftingStep = 1 | 2 | 3 | 4 | 5;
export function GiftingScreen({
  onNavigate
}: GiftingScreenProps) {
  const [step, setStep] = useState<GiftingStep>(1);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedItem, setSelectedItem] = useState<Product | null>(null);
  const [recipient, setRecipient] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [showReceivedExpanded, setShowReceivedExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [recipientMode, setRecipientMode] = useState<'contacts' | 'userid'>('contacts');
  const {
    showToast
  } = useToast();
  const receivedGifts = MOCK_GIFTS.filter(g => g.recipient === 'You');
  const pendingCount = receivedGifts.filter(g => g.status === 'pending').length;
  // Mock contacts
  const mockContacts = [{
    id: '1',
    name: 'Emma Thompson',
    phone: '+1 (555) 123-4567'
  }, {
    id: '2',
    name: 'James Wilson',
    phone: '+1 (555) 234-5678'
  }, {
    id: '3',
    name: 'Sophia Chen',
    phone: '+1 (555) 345-6789'
  }, {
    id: '4',
    name: 'Marcus Johnson',
    phone: '+1 (555) 456-7890'
  }];
  const handleStoreSelect = (store: Store) => {
    setSelectedStore(store);
    setActiveCategory('All');
    setStep(2);
  };
  const handleItemSelect = (product: Product) => {
    setSelectedItem(product);
    setStep(3);
  };
  const handleContinueToConfirm = () => {
    if (!recipient) {
      showToast('Please select a recipient', 'error');
      return;
    }
    setStep(4);
  };
  const handleSendGift = () => {
    setStep(5); // Go to success screen
  };
  const handleBackToHome = () => {
    // Reset all state
    setStep(1);
    setSelectedStore(null);
    setSelectedItem(null);
    setRecipient('');
    setRecipientPhone('');
    setMessage('');
    setActiveCategory('All');
    onNavigate('home');
  };
  const handleBack = () => {
    if (step === 2) {
      setSelectedStore(null);
      setStep(1);
    } else if (step === 3) {
      setSelectedItem(null);
      setStep(2);
    } else if (step === 4) {
      setStep(3);
    } else if (step === 5) {
      handleBackToHome();
    }
  };
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const filteredStores = STORES.filter(store => store.name.toLowerCase().includes(searchQuery.toLowerCase()) || store.storeId.toLowerCase().includes(searchQuery.toLowerCase()));
  const [message, setMessage] = useState('');
  // Step 5: Success Screen
  if (step === 5) {
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-gradient-to-br from-purple-500 to-purple-700 flex flex-col" data-id="element-287">
        {/* Back Button */}
        <button onClick={handleBack} className="mb-6 w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-288">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-289" />
        </button>

        {/* Success Content */}
        <div className="flex-1 flex flex-col items-center justify-start pt-12" data-id="element-290">
          {/* Success Icon */}
          <div className="w-32 h-32 bg-lime-200 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500" data-id="element-291">
            <PartyPopper size={64} className="text-lime-600" data-id="element-292" />
          </div>

          {/* Success Message */}
          <div className="text-center mb-8" data-id="element-293">
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-2" data-id="element-294">
              🎉 Gift Sent!
            </h1>
            <p className="text-purple-100 text-base" data-id="element-295">
              Your gift has been sent to
            </p>
          </div>

          {/* Recipient Card */}
          <div className="w-full bg-white rounded-2xl p-5 mb-4 text-center shadow-lg" data-id="element-296">
            <p className="font-bold text-gray-900 text-xl mb-1" data-id="element-297">{recipient}</p>
            <p className="text-gray-500 text-sm" data-id="element-298">{recipientPhone}</p>
          </div>

          {/* Action Buttons */}
          <div className="w-full space-y-3" data-id="element-299">
            <button className="w-full py-4 bg-white/20 backdrop-blur-sm text-white rounded-2xl font-bold text-base active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-300">
              <LinkIcon size={20} data-id="element-301" />
              Share Gift Link
            </button>

            <button onClick={handleBackToHome} className="w-full py-4 bg-cyan-500 text-white rounded-2xl font-bold text-base active:scale-[0.98] transition-transform shadow-lg" data-id="element-302">
              Back to Home
            </button>

            <button onClick={() => onNavigate('gift-history')} className="w-full py-3 text-purple-200 font-semibold text-base" data-id="element-303">
              View Gift History
            </button>
          </div>
        </div>
      </div>;
  }
  // Step 1: Store Selection + Gifts Received
  if (step === 1) {
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-gradient-to-br from-purple-500 to-purple-700" data-id="element-304">
        {/* Back Button */}
        <button onClick={() => onNavigate('home')} className="mb-6 w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-305">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-306" />
        </button>

        {/* Main Purple Card */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-6 mb-6 shadow-2xl" data-id="element-307">
          {/* Select a Store Heading */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-308">
            Select a Store
          </h2>

          {/* Search Bar */}
          <div className="relative mb-4" data-id="element-309">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-310" />
            <input type="text" placeholder="Search by store name or place ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 text-base" data-id="element-311" />
          </div>

          {/* Store List (Vertical) */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto" data-id="element-312">
            {filteredStores.map(store => <button key={store.id} onClick={() => handleStoreSelect(store)} className="w-full bg-white rounded-2xl p-4 flex items-center gap-4 text-left active:scale-[0.98] transition-transform shadow-sm" data-id="element-313">
                <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-314">
                  <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-315" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-316">
                  <h3 className="font-bold text-gray-900 text-base mb-1 truncate" data-id="element-317">
                    {store.name}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-318">ID: {store.storeId}</p>
                </div>
              </button>)}
          </div>
        </div>

        {/* Gifts Received Section */}
        <div className="bg-gradient-to-br from-[#9DC817] to-[#8AB614] rounded-3xl p-6 shadow-xl" data-id="element-319">
          <div className="flex items-center gap-2 mb-3" data-id="element-320">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-321">Gifts Received</h2>
            {pendingCount > 0 && <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1" data-id="element-322">
                <GiftIcon size={12} data-id="element-323" />
                {pendingCount}
              </span>}
          </div>
          <p className="text-gray-700 text-base mb-4" data-id="element-324">
            Gifts you've received from friends
          </p>

          {/* Gifts List */}
          <div className="space-y-3 mb-4" data-id="element-325">
            {receivedGifts.map(gift => <div key={gift.id} className={`rounded-2xl p-4 ${gift.status === 'pending' ? 'bg-lime-100' : 'bg-white'}`} data-id="element-326">
                <div className="flex items-start gap-3" data-id="element-327">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-328">
                    <img src={gift.itemImage} alt={gift.itemName} className="w-full h-full object-cover" data-id="element-329" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-330">
                    <h3 className="font-bold text-gray-900 text-base mb-1 truncate" data-id="element-331">
                      {gift.itemName}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2 truncate" data-id="element-332">
                      {gift.storeName}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-600 mb-2" data-id="element-333">
                      <span className="flex items-center gap-1" data-id="element-334">
                        <GiftIcon size={12} className="text-[#F7C300]" data-id="element-335" />
                        From {gift.sender}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-336">
                        📅 {formatDate(gift.date)}
                      </span>
                    </div>
                    {gift.status === 'pending' ? <button onClick={() => onNavigate('gift-redeem', gift)} className="px-4 py-2 bg-[#FFFC00] text-gray-900 rounded-xl text-sm font-bold active:scale-95 transition-transform" data-id="element-337">
                        Redeem Now
                      </button> : <div className="flex items-center gap-1 text-sm text-green-700 font-medium" data-id="element-338">
                        <Check size={16} className="text-green-700" data-id="element-339" />
                        Redeemed
                      </div>}
                  </div>
                </div>
              </div>)}
          </div>

          {/* View Gift History Button */}
          <button onClick={() => onNavigate('gift-history')} className="w-full py-3 bg-[#FFFC00] text-gray-900 rounded-2xl font-bold text-base active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-340">
            <GiftIcon size={18} data-id="element-341" />
            View Gift History
          </button>
        </div>
      </div>;
  }
  // Steps 2 & 3: Item Selection and Recipient Selection (Same Screen)
  if ((step === 2 || step === 3) && selectedStore) {
    const products = PRODUCTS[selectedStore.id] || PRODUCTS['1'];
    const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];
    const filteredProducts = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-gradient-to-br from-purple-500 to-purple-700" data-id="element-342">
        {/* Back Button */}
        <button onClick={handleBack} className="mb-6 w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-343">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-344" />
        </button>

        {/* Main Purple Card */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-6 shadow-2xl" data-id="element-345">
          {/* Header */}
          <div className="mb-6" data-id="element-346">
            <h1 className="text-3xl font-bold text-white mb-2" data-id="element-347">Send a Gift</h1>
            <p className="text-purple-100 text-base" data-id="element-348">
              Choose a store and send a gift to friends
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex justify-between mb-6" data-id="element-349">
            {[1, 2, 3].map(stepNumber => <div key={stepNumber} className={`flex items-center justify-center rounded-full w-12 h-12 font-bold text-base transition-all ${stepNumber < step ? 'bg-[#FFFC00] text-black' : stepNumber === step ? 'bg-[#FFFBE6] text-[#F7C300] border-2 border-[#FFFC00]' : 'bg-white/20 text-white/60'}`} data-id="element-350">
                {stepNumber < step ? <Check size={16} strokeWidth={3} data-id="element-351" /> : stepNumber}
              </div>)}
          </div>

          {/* Step 2: Item Selection */}
          {step === 2 && <div className="animate-in fade-in duration-300" data-id="element-352">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-353">
                Choose an Item
              </h2>

              {/* Product Grid */}
              <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto mb-6" data-id="element-354">
                {filteredProducts.map(product => <button key={product.id} onClick={() => handleItemSelect(product)} className="bg-white rounded-2xl p-3 text-left active:scale-[0.98] transition-transform shadow-sm" data-id="element-355">
                    <div className="aspect-square rounded-xl bg-gray-100 mb-3 overflow-hidden" data-id="element-356">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-357" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1" data-id="element-358">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2" data-id="element-359">
                      {product.description}
                    </p>
                    <p className="text-base font-bold text-purple-600" data-id="element-360">
                      ${product.price.toFixed(2)}
                    </p>
                  </button>)}
              </div>
            </div>}

          {/* Step 3: Recipient Selection */}
          {step === 3 && selectedItem && <div className="animate-in fade-in duration-300" data-id="element-361">
              {/* Selected Item Preview */}
              <div className="bg-white rounded-2xl p-4 mb-4 flex items-center gap-3" data-id="element-362">
                <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-363">
                  <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover" data-id="element-364" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-365">
                  <h3 className="font-bold text-gray-900 text-base truncate" data-id="element-366">
                    {selectedItem.name}
                  </h3>
                  <p className="text-purple-600 font-bold text-base" data-id="element-367">
                    ${selectedItem.price.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Message Input */}
              <textarea className="w-full rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 p-4 mb-4 text-base resize-none" placeholder="Write a personal message..." value={message} onChange={e => setMessage(e.target.value)} rows={4} data-id="element-368" />

              {/* Select Recipient Heading */}
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-369">
                Select Recipient
              </h2>

              {/* Recipient Mode Toggle */}
              <div className="flex gap-2 mb-4" data-id="element-370">
                <button onClick={() => setRecipientMode('contacts')} className={`flex-1 py-3 rounded-2xl font-bold text-base transition-colors ${recipientMode === 'contacts' ? 'bg-white text-gray-900' : 'bg-white/20 text-white'}`} data-id="element-371">
                  From Contacts
                </button>
                <button onClick={() => setRecipientMode('userid')} className={`flex-1 py-3 rounded-2xl font-bold text-base transition-colors ${recipientMode === 'userid' ? 'bg-white text-gray-900' : 'bg-white/20 text-white'}`} data-id="element-372">
                  Enter User ID
                </button>
              </div>

              {recipientMode === 'contacts' ? <>
                  {/* Search Contacts */}
                  <div className="relative mb-4" data-id="element-373">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-374" />
                    <input type="text" placeholder="Search contacts..." className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 text-base" data-id="element-375" />
                  </div>

                  {/* Contacts List */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto mb-6" data-id="element-376">
                    {mockContacts.map(contact => <button key={contact.id} onClick={() => {
                setRecipient(contact.name);
                setRecipientPhone(contact.phone);
                setStep(4);
              }} className="w-full bg-white rounded-2xl p-4 flex items-center gap-3 text-left active:scale-[0.98] transition-all" data-id="element-377">
                        <div className="w-10 h-10 bg-[#FFFC00] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-378">
                          <User size={20} className="text-gray-900" data-id="element-379" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-380">
                          <p className="font-bold text-gray-900 text-base truncate" data-id="element-381">
                            {contact.name}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-382">
                            {contact.phone}
                          </p>
                        </div>
                      </button>)}
                  </div>
                </> : <div className="mb-6" data-id="element-383">
                  <input type="text" placeholder="Enter user ID..." value={recipient} onChange={e => setRecipient(e.target.value)} className="w-full py-3.5 px-4 rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300 text-base mb-4" data-id="element-384" />

                  {/* Continue Button for User ID mode */}
                  <button onClick={handleContinueToConfirm} className="w-full py-4 bg-[#FFFC00] text-gray-900 rounded-2xl font-bold text-lg active:scale-[0.98] transition-transform shadow-lg" data-id="element-385">
                    Continue
                  </button>
                </div>}
            </div>}
        </div>
      </div>;
  }
  // Step 4: Confirmation
  if (step === 4 && selectedItem && selectedStore) {
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-gradient-to-br from-purple-500 to-purple-700" data-id="element-386">
        {/* Back Button */}
        <button onClick={handleBack} className="mb-6 w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-387">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-388" />
        </button>

        {/* Confirmation Card */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-6 shadow-2xl" data-id="element-389">
          <div className="bg-white rounded-3xl p-6" data-id="element-390">
            {/* Confirm Gift Heading */}
            <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-391">
              Confirm Gift
            </h1>

            {/* Selected Item */}
            <div className="flex items-center gap-4 mb-6" data-id="element-392">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-393">
                <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover" data-id="element-394" />
              </div>
              <div data-id="element-395">
                <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-396">
                  {selectedItem.name}
                </h2>
                <p className="text-lg font-bold text-purple-600" data-id="element-397">
                  ${selectedItem.price.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Sending To */}
            <div className="mb-6" data-id="element-398">
              <p className="text-gray-600 text-base mb-3" data-id="element-399">Sending to:</p>
              <div className="flex items-center gap-3" data-id="element-400">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-401">
                  <User size={24} className="text-cyan-600" data-id="element-402" />
                </div>
                <div data-id="element-403">
                  <p className="font-bold text-gray-900 text-lg" data-id="element-404">{recipient}</p>
                  <p className="text-gray-500 text-sm" data-id="element-405">{recipientPhone}</p>
                </div>
              </div>
            </div>

            {/* Expiry Warning */}
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-id="element-406">
              <GiftIcon size={20} className="text-orange-600 flex-shrink-0 mt-0.5" data-id="element-407" />
              <p className="text-orange-800 text-sm font-medium" data-id="element-408">
                Gift will expire in 1 week if not redeemed
              </p>
            </div>

            {/* Send Gift Button */}
            <button onClick={handleSendGift} className="w-full py-4 bg-[#FFFC00] text-gray-900 rounded-2xl font-bold text-lg active:scale-[0.98] transition-transform shadow-lg mb-3" data-id="element-409">
              Send Gift
            </button>

            {/* Choose Different Recipient */}
            <button onClick={() => setStep(3)} className="w-full py-3 text-gray-600 font-semibold text-base" data-id="element-410">
              Choose Different Recipient
            </button>
          </div>
        </div>
      </div>;
  }
  return null;
}